import React from 'react'

import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'

import {
  ANALYTICS_EVENTS,
  Loader,
  MicroWidgetContainer,
  useAccountStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useGoalStore,
  useWidgetLoadTimer,
} from '../common'

import { GoalMicroStatusCard } from './components/micro/GoalMicroStatusCard'
import { GoalsMicroEmptyState } from './components/micro/GoalsMicroEmptyState'

interface GoalsMicroWidgetProps {
  onCTAClick: () => void
}

export const GoalsMicroWidget = observer(({ onCTAClick }: GoalsMicroWidgetProps) => {
  const { isAccountDataLoaded, loadAccountData, visibleAccounts: accounts } = useAccountStore()
  const { onEvent } = useEvent()
  const { goals: copy } = useGlobalCopyStore()
  const { isInitialized } = useGlobalUiStore()
  const { ongoingGoalsForMicroWidget: goals, isAllGoalDataLoaded, loadGoalData } = useGoalStore()

  useWidgetLoadTimer({
    widgetName: 'GoalsMicroWidget',
    isLoaded: isAllGoalDataLoaded,
  })

  React.useEffect(() => {
    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
  }, [])

  React.useEffect(() => {
    if (isInitialized) {
      loadGoalData().finally()
      onEvent(ANALYTICS_EVENTS.GOALS_LOAD_WIDGET, {
        state: !accounts?.length ? 'zeroState' : 'default',
      })
    }
  }, [isInitialized])

  const isEmptyGoalsState = React.useMemo(
    () => isAllGoalDataLoaded && goals.length === 0,
    [goals, isAllGoalDataLoaded],
  )

  const handleCTAClick = () => {
    onEvent(
      isEmptyGoalsState
        ? ANALYTICS_EVENTS.GOALS_CLICK_GET_STARTED
        : ANALYTICS_EVENTS.GOALS_CLICK_VIEW_DETAILS,
    )
    onCTAClick()
  }

  if (!isInitialized || !isAllGoalDataLoaded) {
    return <Loader />
  }

  return (
    <MicroWidgetContainer
      className="mx-goals-microwidget"
      ctaLabel={!isEmptyGoalsState ? copy.view_details : copy.get_started}
      onCTAClick={handleCTAClick}
      title={copy.title}
    >
      {!isEmptyGoalsState ? (
        <Stack sx={{ justifyContent: 'space-between' }}>
          {goals.map((goal, index) => (
            <React.Fragment key={goal.guid}>
              <GoalMicroStatusCard goal={goal} />
              {index !== goals.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Stack>
      ) : (
        <GoalsMicroEmptyState
          header={copy.create_goals_header}
          subText={copy.micro_zero_state_description}
        />
      )}
    </MicroWidgetContainer>
  )
})
